import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { toast } from 'react-toastify';

interface NotificationConfig {
    prompt: string;
    keywords?: string[];
}

interface AimdocNotificationsModalProps {
    isOpen: boolean;
    closeModal: () => void;
    notificationType: 'escalations' | 'buyer_intent';
    value?: NotificationConfig;
    onSave: (config: NotificationConfig) => void;
}

export default function AimdocNotificationsModal({
    isOpen,
    closeModal,
    notificationType,
    value,
    onSave
}: AimdocNotificationsModalProps) {
    const [prompt, setPrompt] = useState(value?.prompt || '');
    const [keywords, setKeywords] = useState(value?.keywords?.join(', ') || '');

    useEffect(() => {
        if (isOpen) {
            setPrompt(value?.prompt || '');
            setKeywords(value?.keywords?.join(', ') || '');
        }
    }, [isOpen, value]);

    const handleSave = () => {
        // For escalations, prompt is required
        // For buyer_intent, either prompt OR keywords is required
        if (notificationType === 'escalations' && !prompt.trim()) {
            toast.error('Please enter a prompt.');
            return;
        }
        
        if (notificationType === 'buyer_intent' && !prompt.trim() && !keywords.trim()) {
            toast.error('Please enter AI detection conditions or trigger keywords.');
            return;
        }

        const config: NotificationConfig = {
            prompt: prompt.trim()
        };

        // Add keywords for buyer intent
        if (notificationType === 'buyer_intent' && keywords.trim()) {
            config.keywords = keywords
                .split(',')
                .map(k => k.trim().toLowerCase())
                .filter(k => k.length > 0);
        }

        onSave(config);
        closeModal();
    };

    const handleClose = () => {
        closeModal();
    };

    const getTitle = () => {
        return notificationType === 'escalations' 
            ? 'Configure Escalations' 
            : 'Configure Buyer Intent';
    };

    const getPromptPlaceholder = () => {
        if (notificationType === 'escalations') {
            return 'Describe the conditions when the AI should escalate to a human agent. For example: "When the user asks complex technical questions that require expert knowledge, or when they express frustration with the current conversation."';
        }
        return 'Describe the conditions when the AI should identify buyer intent. For example: "When the user asks about pricing, requests a demo, mentions budget, or expresses interest in purchasing or upgrading."';
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>{getTitle()}</DialogTitle>
                </DialogHeader>

                <Alert className="mt-4">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                        <strong>Reminder:</strong> Make sure to enable the {notificationType === 'escalations' ? 'escalation' : 'buyer intent'} notification setting 
                        in your <a href="/user-settings" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">user settings</a> for 
                        team members who need to receive these notifications.
                    </AlertDescription>
                </Alert>

                <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="prompt">
                            AI Detection Conditions
                            {notificationType === 'buyer_intent' && <span className="text-gray-400 font-normal ml-1">(or use keywords below)</span>}
                        </Label>
                        <Textarea
                            id="prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={getPromptPlaceholder()}
                            rows={6}
                            className="w-full resize-none"
                        />
                        <p className="text-sm text-gray-500">
                            {notificationType === 'escalations' 
                                ? 'The AI will use this prompt to determine when to trigger escalations.'
                                : 'The AI will use this prompt to determine when to trigger buyer intent notifications. Leave empty to use keywords only.'}
                        </p>
                    </div>

                    {notificationType === 'buyer_intent' && (
                        <div className="space-y-2">
                            <Label htmlFor="keywords">
                                Trigger Keywords
                                {!prompt.trim() && <span className="text-gray-400 font-normal ml-1">(required if no AI conditions)</span>}
                            </Label>
                            <Input
                                id="keywords"
                                value={keywords}
                                onChange={(e) => setKeywords(e.target.value)}
                                placeholder="pricing, demo, purchase, buy, quote, cost, trial, upgrade, budget"
                                className="w-full"
                            />
                            <p className="text-sm text-gray-500">
                                Comma-separated keywords that instantly trigger notifications when mentioned by visitors. Use keywords alone for simple detection, or combine with AI conditions for comprehensive coverage.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave}
                        disabled={notificationType === 'escalations' ? !prompt.trim() : (!prompt.trim() && !keywords.trim())}
                    >
                        Save Configuration
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
} 